import { Box, IconButton, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { NotificationSeverity } from './data';

export const Container = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  boxSizing: 'border-box',
  backgroundColor: theme.palette.common.white,
}));

export const Header = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 0,
  padding: '24px 40px 8px',
}));

// `minHeight: 0` é o que permite o filho encolher abaixo do conteúdo dentro do
// flex do painel; sem isso a área cresce e o scroll volta para o paper.
export const ScrollArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '0 40px 12px',

  '&::-webkit-scrollbar': {
    width: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.primary.dark,
    borderRadius: '10px',

    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  alignSelf: 'flex-end',
  color: theme.palette.common.grey,
  margin: '45px 0 67px',
}));

export const PanelTitle = styled(Typography)(() => ({
  color: '#1a1a1a',
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: '140%',
  margin: '16px 0 24px',
}));

// O Search aplica a largura no input interno; o root precisa acompanhar para
// o campo ocupar o painel inteiro.
export const SearchBox = styled(Box)(() => ({
  '& .MuiInputBase-root': {
    width: '100%',
    boxSizing: 'border-box',
  },
}));

export const ChipList = styled(Box)(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  margin: '24px 0 8px',
}));

export const FilterChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ theme, isActive }) => ({
  padding: '6px 12px',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '13px',
  lineHeight: '140%',
  whiteSpace: 'nowrap',
  border: `1px solid ${theme.palette.secondary.main}`,
  backgroundColor: isActive
    ? theme.palette.secondary.main
    : theme.palette.common.white,
  color: isActive ? theme.palette.common.white : theme.palette.secondary.main,

  '&:hover': {
    opacity: 0.8,
    transition: '0.3s',
  },
}));

export const GroupLabel = styled(Typography)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  color: '#6B7280',
  fontSize: '13px',
  lineHeight: '140%',
  margin: '24px 0 12px',

  '&::before, &::after': {
    content: '""',
    flex: 1,
    height: '1px',
    backgroundColor: '#E3E3E3',
  },
}));

// Só a não lida tem ação: no hover, o botão de marcar como lida entra no lugar
// da hora e da bolinha.
export const NotificationCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isRead',
})<{ isRead: boolean }>(({ isRead }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px',
  marginBottom: '12px',
  borderRadius: '8px',
  border: '1px solid #E3E3E3',
  backgroundColor: '#FFF',
  cursor: 'pointer',

  '& .notification-read': {
    display: 'none',
  },

  '&:hover': {
    backgroundColor: '#47546710',
    transition: '0.3s',

    ...(!isRead && {
      '& .notification-meta': {
        display: 'none',
      },

      '& .notification-read': {
        display: 'inline-flex',
      },
    }),
  },
}));

export const ReadButton = styled(IconButton)(({ theme }) => ({
  padding: '4px',
  color: theme.palette.secondary.main,

  '& span': {
    fontSize: '20px',
  },
}));

export const LoadMoreBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'center',
  margin: '8px 0 16px',
}));

const mutedIcon = '#9CA3AF';

// Cinza é o neutro: lida, ou evento que não pede ação.
export const CardIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'severity' && prop !== 'isRead',
})<{ severity: NotificationSeverity; isRead: boolean }>(
  ({ theme, severity, isRead }) => ({
    display: 'flex',
    alignItems: 'center',
    color: isRead
      ? mutedIcon
      : {
          success: theme.palette.secondary.main,
          error: theme.palette.common.danger,
          warning: '#FFD113',
          info: mutedIcon,
        }[severity],

    '& span': {
      fontSize: '22px',
    },
  })
);

export const CardText = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
  flex: 1,
  minWidth: 0,
}));

export const CardTitle = styled(Typography)(() => ({
  color: '#1a1a1a',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '140%',
}));

export const CardClient = styled(Typography)(() => ({
  color: '#6B7280',
  fontSize: '12px',
  lineHeight: '140%',
}));

export const CardMeta = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
}));

export const CardTime = styled(Typography)(() => ({
  color: '#6B7280',
  fontSize: '12px',
  lineHeight: '140%',
  whiteSpace: 'nowrap',
}));

export const UnreadDot = styled(Box)(({ theme }) => ({
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: theme.palette.common.danger,
}));

export const EmptyMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.common.grey,
  fontSize: '14px',
  textAlign: 'center',
  margin: '40px 0',
}));
